import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, RefreshCw, Server, Tag } from 'lucide-react';
import { Banner, Button, EmptyState, Field, Input } from '@/components/ui';
import type { ConnectFormProps } from '@/components/nodes/ConnectDrawer';
import { useRoles } from '@/hooks/useAuth';
import { useRegisterNode, useServerInventory, useSyncServers } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { can } from '@/utils/permissions';
import { isSlug, splitList } from '@/utils/validation';
import type { ServerInventoryView } from '@/types/api';

function suggestName(server: ServerInventoryView): string {
  const base = (server.displayName ?? server.hostname).toLowerCase().split('.')[0] ?? '';
  return base.replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63);
}

/**
 * A server of the panel's own (TZ section 10.5): one from the 2d2s inventory
 * that becomes a node the panel places sites on.
 *
 * Servers come from 2d2s, only those carrying the configured tag (ADR-0002).
 * The panel never adds a server itself and never tags one: that is done in
 * 2d2s, by a person who knows what already runs there.
 */
export function ConnectNodeForm({ formId, onIssued, onStatus }: ConnectFormProps): ReactNode {
  const roles = useRoles();

  const [serverId, setServerId] = useState('');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [labels, setLabels] = useState('wordpress');
  const [maxSites, setMaxSites] = useState('20');
  const [reservedMemory, setReservedMemory] = useState('2048');
  const [reservedDisk, setReservedDisk] = useState('40');
  const [shown, setShown] = useState(false);
  // After a sync is queued, the list is asked again for a while: the worker
  // reads 2d2s in the background and the form has no other way to learn
  // when it is done.
  const [watchUntil, setWatchUntil] = useState(0);

  const inventory = useServerInventory({ enabled: true, pollUntil: watchUntil });

  const servers = inventory.data?.servers ?? [];
  const free = servers.filter((server) => !server.nodeId);
  const chosen = free.find((server) => server.externalId === serverId) ?? null;
  const effectiveName = nameTouched ? name : chosen ? suggestName(chosen) : '';

  const problems: Partial<Record<'server' | 'name' | 'numbers', string>> = {};
  if (!chosen) problems.server = 'Выберите сервер';
  if (!isSlug(effectiveName) || effectiveName.length < 2) problems.name = 'Латиница в нижнем регистре, цифры и дефис; от двух символов';
  if (![maxSites, reservedMemory, reservedDisk].every((value) => /^\d+$/.test(value)) || Number(maxSites) < 1) {
    problems.numbers = 'Только целые числа; сайтов — не меньше одного';
  }

  const sync = useSyncServers({
    onSuccess: () => {
      setWatchUntil(Date.now() + 30_000);
      toast.success('Синхронизация с 2d2s запущена');
    },
  });

  const register = useRegisterNode({ onSuccess: onIssued });
  const blamed = register.isError ? errorField(register.error) : null;

  useEffect(() => onStatus({ ready: free.length > 0, busy: register.isPending }), [free.length, register.isPending, onStatus]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setShown(true);
    if (Object.keys(problems).length > 0 || register.isPending) return;
    register.mutate({
      serverExternalId: serverId,
      name: effectiveName,
      labels: splitList(labels),
      maxSites: Number(maxSites),
      reservedMemoryMb: Number(reservedMemory),
      reservedDiskGb: Number(reservedDisk),
    });
  };

  const mayManage = can(roles, 'nodes.manage');

  return (
    <form id={formId} className="stack" onSubmit={submit} noValidate>
      {inventory.data?.scopeTag ? (
        <Banner tone="info" icon={Tag}>
          <div className="stack stack-tight">
            <span>Показаны серверы 2d2s с тегом «{inventory.data.scopeTag}».</span>
            {inventory.data.syncAvailable && mayManage ? (
              <div className="row-tight">
                <Button size="sm" icon={RefreshCw} busy={sync.isPending} onClick={() => sync.mutate()}>
                  Обновить из 2d2s
                </Button>
              </div>
            ) : null}
          </div>
        </Banner>
      ) : (
        <Banner tone="warning" icon={CircleAlert}>
          Интеграция с 2d2s не настроена: не заданы ключ API или тег серверов.
        </Banner>
      )}

      {sync.isError ? (
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(sync.error, 'Не удалось запросить обновление')}
        </p>
      ) : null}

      <fieldset className="field fieldset">
        <legend className="field-label">Сервер</legend>
        {inventory.isPending ? (
          <p className="field-hint">Загрузка…</p>
        ) : free.length === 0 ? (
          <EmptyState icon={Server} message={servers.length === 0 ? 'Серверов нет' : 'Все серверы уже подключены'} />
        ) : (
          <div className="choice-list" role="radiogroup" aria-label="Сервер">
            {free.map((server) => (
              <label key={server.externalId} className={`choice${serverId === server.externalId ? ' choice-active' : ''}`}>
                <input
                  type="radio"
                  name="server"
                  value={server.externalId}
                  checked={serverId === server.externalId}
                  onChange={() => setServerId(server.externalId)}
                />
                <span className="stack stack-tight">
                  <span className="strong">{server.displayName ?? server.hostname}</span>
                  <span className="text-sm muted mono">{[server.ipv4, server.datacenter].filter(Boolean).join(' · ') || server.hostname}</span>
                  <span className="text-sm muted">
                    {[
                      server.memoryMb ? `${Math.round(server.memoryMb / 1024)} ГБ памяти` : null,
                      server.diskGb ? `${server.diskGb} ГБ диска` : null,
                      server.vcpuCount ? `${server.vcpuCount} vCPU` : null,
                      `обновлено ${formatRelative(server.syncedAt)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
        {shown && problems.server && free.length > 0 ? <p className="field-error">{problems.server}</p> : null}
        {blamed === 'serverExternalId' ? <p className="field-error">{errorText(register.error)}</p> : null}
      </fieldset>

      <Field
        label="Имя в панели"
        htmlFor="node-name"
        hint="Отображается в списках и задачах"
        {...(shown && problems.name ? { error: problems.name } : {})}
        {...(blamed === 'name' ? { error: errorText(register.error) } : {})}
      >
        <Input
          id="node-name"
          className="mono"
          value={effectiveName}
          onChange={(event) => {
            setNameTouched(true);
            setName(event.target.value.toLowerCase());
          }}
          maxLength={63}
          spellCheck={false}
          autoComplete="off"
        />
      </Field>

      <Field label="Метки" htmlFor="node-labels" hint="Через запятую; используются при выборе сервера для сайта">
        <Input id="node-labels" className="mono" value={labels} onChange={(event) => setLabels(event.target.value)} spellCheck={false} autoComplete="off" />
      </Field>

      <div className="form-grid">
        <Field label="Сайтов не больше" htmlFor="node-max-sites">
          <Input id="node-max-sites" inputMode="numeric" value={maxSites} onChange={(event) => setMaxSites(event.target.value)} />
        </Field>
        <Field label="Резерв памяти, МБ" htmlFor="node-reserve-memory" hint="Не используется для сайтов">
          <Input id="node-reserve-memory" inputMode="numeric" value={reservedMemory} onChange={(event) => setReservedMemory(event.target.value)} />
        </Field>
        <Field
          label="Резерв диска, ГБ"
          htmlFor="node-reserve-disk"
          hint="Для копий и роста данных"
          {...(shown && problems.numbers ? { error: problems.numbers } : {})}
        >
          <Input id="node-reserve-disk" inputMode="numeric" value={reservedDisk} onChange={(event) => setReservedDisk(event.target.value)} />
        </Field>
      </div>

      {register.isError && !blamed ? (
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(register.error, 'Не удалось подключить сервер')}
        </p>
      ) : null}
    </form>
  );
}
